package com.tovtalk.ui.screen.hashtag

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PlayCircleOutline
import androidx.compose.material.icons.filled.Tag
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.hashtag.HashtagViewModel
import com.tovtalk.ui.adaptive.isCompact
import com.tovtalk.ui.adaptive.responsiveGridColumns
import com.tovtalk.ui.components.BannerAdRow
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.components.TovTalkAsyncImage
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import com.tovtalk.ui.theme.TovTalkTheme

private sealed class HashtagGridItem {
    data class PostItem(val post: Post) : HashtagGridItem()
    data object BannerItem : HashtagGridItem()
}

@Composable
fun HashtagScreen(
    hashtag: String,
    viewModel: HashtagViewModel,
    onPostClick: (Post, Int) -> Unit,
    onBack: () -> Unit,
    adsRepository: AdsRepository? = koinInject(),
) {
    val postsState by viewModel.posts.collectAsState()
    val gridState = rememberLazyGridState()

    var bannerUrl by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(Unit) {
        bannerUrl = runCatching { adsRepository?.buildBannerUrl() }.getOrNull()
    }

    // Load more when near end
    val shouldLoadMore by remember {
        derivedStateOf {
            val lastVisibleItem = gridState.layoutInfo.visibleItemsInfo.lastOrNull()
            lastVisibleItem != null &&
                lastVisibleItem.index >= gridState.layoutInfo.totalItemsCount - 6
        }
    }
    LaunchedEffect(shouldLoadMore) {
        if (shouldLoadMore) viewModel.loadMore()
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = "#$hashtag",
                onBack = onBack,
            )
        },
        constrainContentWidth = false,
    ) { padding ->
        val hashtagContent = @Composable {
        when (val state = postsState) {
            is UiState.Loading -> {
                Box(Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }
            is UiState.Error -> {
                Box(Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                    Text(state.message)
                }
            }
            is UiState.Success -> {
                if (state.data.isEmpty()) {
                    Box(Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                        Text(
                            text = "אין תוצאות עבור #$hashtag",
                            style = TovTalkTheme.typography.bodyLarge,
                            color = TovTalkTheme.colors.textSecondary
                        )
                    }
                } else {
                    val columns = responsiveGridColumns(
                        compact = 3,
                        mediumCompact = 3,
                        medium = 4,
                        expanded = 5,
                        large = 6,
                        extraLarge = 8
                    )

                    val postsPerBannerBlock = columns * 3

                    val gridItems = remember(state.data, postsPerBannerBlock) {
                        val result = mutableListOf<HashtagGridItem>()
                        state.data.forEachIndexed { index, post ->
                            result.add(HashtagGridItem.PostItem(post))
                            if ((index + 1) % postsPerBannerBlock == 0) {
                                result.add(HashtagGridItem.BannerItem)
                            }
                        }
                        result
                    }

                    LazyVerticalGrid(
                        columns = GridCells.Fixed(columns),
                        state = gridState,
                        modifier = Modifier.fillMaxSize().padding(padding),
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        contentPadding = PaddingValues(horizontal = 4.dp),
                    ) {
                        // Hashtag header — vertical on Compact, horizontal on MediumCompact+
                        item(span = { GridItemSpan(maxLineSpan) }) {
                            HashtagHeader(hashtag = hashtag)
                        }

                        for (gridItem in gridItems) {
                            when (gridItem) {
                                is HashtagGridItem.PostItem -> item(key = gridItem.post.id) {
                                    PostThumbnailItem(
                                        post = gridItem.post,
                                        onClick = {
                                            onPostClick(gridItem.post, state.data.indexOf(gridItem.post))
                                        },
                                    )
                                }
                                HashtagGridItem.BannerItem -> {
                                    val url = bannerUrl
                                    if (url != null) {
                                        item(span = { GridItemSpan(maxLineSpan) }) {
                                            BannerAdRow(url = url)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            else -> {}
        }
        }
        CenteredContentWrapper { hashtagContent() }
    }
}

@Composable
private fun HashtagHeader(hashtag: String) {
    if (isCompact()) {
        // Vertical centered layout for Compact
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
        ) {
            Icon(
                imageVector = Icons.Default.Tag,
                contentDescription = null,
                modifier = Modifier.size(40.dp),
                tint = TovTalkTheme.colors.brand
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "#$hashtag",
                style = TovTalkTheme.typography.titleLarge,
                color = TovTalkTheme.colors.textPrimary
            )
        }
    } else {
        // Horizontal layout centered with maxWidth for MediumCompact+
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.TopCenter
        ) {
            Row(
                modifier = Modifier
                    .widthIn(max = 380.dp)
                    .padding(16.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Tag,
                    contentDescription = null,
                    modifier = Modifier.size(40.dp),
                    tint = TovTalkTheme.colors.brand
                )
                Column {
                    Text(
                        text = "#$hashtag",
                        style = TovTalkTheme.typography.titleLarge,
                        color = TovTalkTheme.colors.textPrimary
                    )
                }
            }
        }
    }
}

@Composable
private fun PostThumbnailItem(
    post: Post,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .aspectRatio(9f / 16f)
            .clip(RoundedCornerShape(8.dp))
            .background(TovTalkTheme.colors.backgroundSecondary)
            .clickable(onClick = onClick),
    ) {
        val thumbnailUrl = post.thumbnail
        if (!thumbnailUrl.isNullOrEmpty()) {
            TovTalkAsyncImage(
                url = thumbnailUrl,
                modifier = Modifier.fillMaxSize(),
                clipToCircle = false,
                contentScale = ContentScale.Crop,
            )
        } else {
            // Placeholder for posts without thumbnail
            Icon(
                Icons.Default.PlayCircleOutline,
                contentDescription = null,
                modifier = Modifier.size(48.dp).align(Alignment.Center),
                tint = TovTalkTheme.colors.textTertiary
            )
        }
        // Author name overlay at bottom
        post.author?.let { author ->
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.BottomStart)
                    .background(TovTalkTheme.colors.backgroundPrimary.copy(alpha = 0.6f))
                    .padding(horizontal = 6.dp, vertical = 4.dp)
            ) {
                Text(
                    text = "@${author.username}",
                    style = TovTalkTheme.typography.labelSmall,
                    color = TovTalkTheme.colors.textPrimary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}
